import { useWorkspaceFieldsRuntime } from '@Pages/pageRuntime/context/WorkspaceFieldsContext';
import { useRef, useSyncExternalStore } from 'react';

/**
 * Reactive read of workspace field values. One call serves many keys, so a page
 * never loops a singular hook. The returned object is referentially stable until one
 * of the requested values changes.
 *
 * Exposed via `Sdk.hooks.useWorkspaceValues(keys)`.
 */
export function useWorkspaceValues<K extends string>(keys: readonly K[]): Record<K, unknown> {
  const { values } = useWorkspaceFieldsRuntime();
  // A page passes an inline array, so the joined key list is the real dependency.
  const keyList = keys.join(' ');
  const cache = useRef<{ keyList: string; snapshot: Record<K, unknown> } | null>(null);

  const getSnapshot = () => {
    const all = values.getSnapshot();
    const cached = cache.current;

    if (
      cached &&
      cached.keyList === keyList &&
      keys.every((key) => cached.snapshot[key] === all[key])
    ) {
      return cached.snapshot;
    }

    const next = {} as Record<K, unknown>;

    for (const key of keys) {
      next[key] = all[key];
    }

    cache.current = { keyList, snapshot: next };

    return next;
  };

  return useSyncExternalStore(values.subscribe, getSnapshot, getSnapshot);
}
